import React, { useMemo } from 'react';
import { Alert, Box } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useQueryClient } from '@tanstack/react-query';
import useApi from '../hooks/useApi';
import { useAuth } from '../auth/googleDomainAuth.jsx';
import useSendTabDraft from '../hooks/product-selection-send/useSendTabDraft';
import useSendTabForm from '../hooks/product-selection-send/useSendTabForm';
import useSendTabNotifications from '../hooks/product-selection-send/useSendTabNotifications';
import useSendTabQueue from '../hooks/product-selection-send/useSendTabQueue';
import useSetDatesPopover from '../hooks/product-selection-send/useSetDatesPopover';
import SelectionFormGrid from './product-selection-send/SelectionFormGrid.jsx';
import SelectionHeader from './product-selection-send/SelectionHeader.jsx';
import SendFloatingAction from './product-selection-send/SendFloatingAction.jsx';
import SendQueueStatus from './product-selection-send/SendQueueStatus.jsx';
import SendSnackbar from './product-selection-send/SendSnackbar.jsx';
import SetDatesPopover from './product-selection-send/SetDatesPopover.jsx';

/**
 * Edit & send tab for a single dealer's product selection.
 * Responsibilities:
 *  - hydrate a selection into the VMT schema
 *  - highlight missing fields
 *  - queue throttled POSTs to /api/vmt/send
 *  - refresh dealer data + send history after each attempt
 *  - surface toasts/snackbars via onNotify
 */
const ProductSelectionSendTab = ({
  selections,
  dealerContext,
  initialSelectionIndex = 0,
  onSelectionChange,
  onNotify = null,
  onRequestTabChange = null,
}) => {
  const queryClient = useQueryClient();
  const { useVmtSend } = useApi();
  const { user } = useAuth();
  const sendMutation = useVmtSend();

  const storageKey = useMemo(() => {
    const bac = dealerContext?.bac || 'unknown';
    const program = dealerContext?.programName || dealerContext?.programLabel || 'unknown';
    return `dpe-send-draft:${program}:${bac}`;
  }, [dealerContext?.bac, dealerContext?.programLabel, dealerContext?.programName]);

  const {
    snackbar,
    showSnackbar,
    emitToast,
    handleCloseSnackbar,
    isMountedRef,
  } = useSendTabNotifications({ onNotify });

  const {
    selectedIndex,
    currentSelection,
    formValues,
    setFormValues,
    errors,
    setErrors,
    baselineSignatureRef,
    hydrateSelectionIntoForm,
    handleSelectionChange,
    handleFieldChange,
    handleDateChange,
    handleUnmatchReferenceId,
    applyPreset,
    handleCancelAndUnmatch,
  } = useSendTabForm({
    selections,
    dealerContext,
    initialSelectionIndex,
    onSelectionChange,
    onRequestTabChange,
  });

  const { clearDraft } = useSendTabDraft({
    storageKey,
    formValues,
    setFormValues,
    baselineSignatureRef,
  });

  const {
    anchorEl: setDatesAnchorEl,
    value: setDatesValue,
    isOpen: isSetDatesOpen,
    handleOpenSetDates,
    handleCloseSetDates,
    applySetDates,
  } = useSetDatesPopover({ formValues, applyPreset });

  const { queue, isProcessing, sendProgress, enqueueSend } = useSendTabQueue({
    formValues,
    dealerContext,
    userEmail: user?.email || null,
    sendMutation,
    queryClient,
    emitToast,
    isMountedRef,
    setErrors,
    clearDraft,
  });

  const hasSelections = selections.length > 0;
  const isBusy = sendMutation.isPending || isProcessing;

  const handleReset = () => {
    clearDraft();
    hydrateSelectionIntoForm(currentSelection);
    showSnackbar('Draft cleared and values reset.', 'info');
  };

  if (!hasSelections) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        This enrollment row does not include any product selections.
      </Alert>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        sx={{
          p: { xs: 1.5, sm: 2 },
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          overflowY: 'auto',
          flex: 1,
        }}
      >
        <SelectionHeader
          selections={selections}
          selectedIndex={selectedIndex}
          onSelectionChange={handleSelectionChange}
          onOpenSetDates={handleOpenSetDates}
          onReset={handleReset}
          isBusy={isBusy}
          onApplyPreset={applyPreset}
          onCancelAndUnmatch={handleCancelAndUnmatch}
        />

        <SetDatesPopover
          anchorEl={setDatesAnchorEl}
          isOpen={isSetDatesOpen}
          value={setDatesValue}
          onClose={handleCloseSetDates}
          onApply={applySetDates}
        />

        <SelectionFormGrid
          formValues={formValues}
          errors={errors}
          onFieldChange={handleFieldChange}
          onDateChange={handleDateChange}
          onUnmatchReferenceId={handleUnmatchReferenceId}
        />

        <SendQueueStatus
          queueLength={queue.length}
          isProcessing={isProcessing}
          sendProgress={sendProgress}
        />

        <SendFloatingAction isBusy={isBusy} onSend={enqueueSend} />

        <SendSnackbar snackbar={snackbar} onClose={handleCloseSnackbar} />
      </Box>
    </LocalizationProvider>
  );
};

export default ProductSelectionSendTab;
